import { CircularProgress, createStyles, makeStyles } from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';
import { NftInclude, ProjectFilter, ProjectSort } from '@mogul/api-client';
import { getOpenseaUrl } from '@mogul/shared';
import React, { useCallback, useContext, useState } from 'react';
import { useHistory } from 'react-router';

import { projectsApi } from '../../../api';
import { getNftIfMinted } from '../../../api-helpers/nft';
import { useAuth } from '../../../auth';
import { ReactComponent as BlackStarIcon } from '../../../common/svg/button-icons/black_star_icon.svg';
import { ReactComponent as QuestionIcon } from '../../../common/svg/button-icons/question-mark-blue.svg';
import { createBuyStarsModal } from '../../../components/buy-stars-modal';
import { CountDown } from '../../../components/common/countdown/CountDown.component';
import { ConnectWalletModal } from '../../../components/common/modal';
import { CollectRewardsSingleRoundModal } from '../../../components/common/modal/movie-voting/CollectRewardsSingleRoundModal.component';
import type { VoteModalProps } from '../../../components/common/modal/movie-voting/VoteModal.component';
import { VoteModal } from '../../../components/common/modal/movie-voting/VoteModal.component';
import { WrongNetworkModal } from '../../../components/common/modal/WrongNetworkModal.component';
import { Button } from '../../../components/core/button';
import type { CastAndCrewData } from '../../../components/movie/card/AdditionalInfo.component';
import type { FinancedMovieData } from '../../../components/movie-voting/FinancedByMogul.component';
import { FinancedByMogul } from '../../../components/movie-voting/FinancedByMogul.component';
import { NotificationCard } from '../../../components/movie-voting/NotificationCard.component';
import { OtherRounds } from '../../../components/movie-voting/OtherRounds.component';
import { ProjectVoting } from '../../../components/movie-voting/ProjectVoting.component';
import { AVERAGE_BLOCK_TIME_BSC } from '../../../constants/voting';
import { ModalStateContext } from '../../../contexts/app-state-context/modal-state.context';
import { useNetwork, useSwitchChain, useWeb3State } from '../../../hooks';
import { useMovieVotingContract } from '../../../hooks/movie-voting.hooks';
import type { MovieRound } from '../../../hooks/voting.hooks';
import {
  emptyMovieRound,
  useVotingContract,
  VotingRoundState,
} from '../../../hooks/voting.hooks';
import {
  CHAIN_ID_TO_TYPE_MAP,
  getCurrentChainKey,
  MOVIE_VOTING_NFT_ADDRESS,
  WEB3_CONFIG,
} from '../../../lib';
import type { ProjectMetadata } from '../project/ProjectPage.container';
import styles from './VotingPage.module.css';

const useStyles = makeStyles(() =>
  createStyles({
    toolTips: {
      maxWidth: '40ch',
      background: '#000000',
      marginLeft: '10px',
    },
    loader: {
      color: 'white',
    },
  }),
);

export const VotingPage: React.FC = () => {
  const history = useHistory();
  const classes = useStyles();
  const { authenticated } = useAuth();
  const { switchNetworkAsync } = useSwitchChain();

  const { setModal } = useContext(ModalStateContext);
  const { walletDetails, isLoading } = useWeb3State();

  const movieVotingContract = useMovieVotingContract({
    overrideAddress: WEB3_CONFIG.contracts.eth.movieVoting,
    overrideProvider: CHAIN_ID_TO_TYPE_MAP[WEB3_CONFIG.chains.eth.id],
    hardOverride: false,
  });

  const votingContract = useVotingContract({
    overrideAddress: WEB3_CONFIG.contracts.bsc.movieVoting,
    overrideProvider: CHAIN_ID_TO_TYPE_MAP[WEB3_CONFIG.chains.bsc.id],
    hardOverride: false,
  });

  const {
    initialized,
    getMovieVotingRounds,
    getUserStarsForMovie,
    claimRewardsForRound,
    getCurrentBlock,
    getBlockTimestamp,
    calculateStarsRewards,
  } =
    walletDetails.chain.id === WEB3_CONFIG.chains.eth.id
      ? movieVotingContract
      : votingContract;

  const [roundAboutToEnd, setRoundAboutToEnd] = useState(false);
  const reloadCallBack = () => {
    setRoundAboutToEnd(true);
  };
  const [estimatedEndDate, setEstimatedEndDate] = useState(new Date());
  const [reloadTimer, setReloadTimer] = useState(new Date());
  const [loading, setLoading] = useState(true);

  const { chain } = useNetwork();

  const [currentRound, setCurrentRound] = useState<MovieRound>(emptyMovieRound);
  const [futureRounds, setFutureRounds] = useState<MovieRound[]>([]);
  const [pastRounds, setPastRounds] = useState<MovieRound[]>([]);

  const [userStars, setUserStars] = useState<string[]>([]);
  const [userStarsReward, setUserStarsReward] = useState<string>('');

  const [isVotingEnded, setIsVotingEnded] = useState<boolean>();

  // MovieIds unique within a round
  const [userVotedState, setUserVotedState] = useState<{
    [movieId: string]: string;
  }>({});
  const [currentBlock, setCurrentBlock] = useState({
    timestamp: 0,
    number: 0,
  });
  const [startBlock, setStartBlock] = useState({
    timestamp: 0,
    number: 0,
  });
  const [endBlock, setEndBlock] = useState({
    timestamp: 0,
    number: 0,
  });
  const [NFTMetaData, setNFTMetaData] = useState<{
    [key: string]: ProjectMetadata;
  }>({});
  const [cast, setCast] = useState<Record<string, CastAndCrewData[]>>({});
  const [totalNumRounds, setTotalNumRounds] = useState(0);

  const [roundStarted, setRoundStarted] = useState(false);
  const [roundEnded, setRoundEnded] = useState(false);

  const [financedMovieData, setFinancedMovieData] = useState<
    FinancedMovieData[]
  >([]);
  const [financedMovieIds, setFinancedMovieIds] = useState<string[]>([]);

  const openseaLink = getOpenseaUrl(
    WEB3_CONFIG.contracts[getCurrentChainKey()].movieVotingNft,
    '',
  );

  const handleClaimRewards = async (roundId: string) => {
    const result = await claimRewardsForRound(roundId);
    return result;
  };

  const handleViewOnClick = (movieId: string) => {
    history.push(`/platform/voting/${currentRound.roundId}/nft/${movieId}`);
  };

  const handleFianancedViewOnClick = (projectId: string) => {
    history.push(`/platform/financedProject/${projectId}`);
  };

  const handleVoteOnClick = (props: VoteModalProps) => {
    if (!walletDetails.address) {
      setModal(ConnectWalletModal());
      return;
    }

    const modal = chain?.unsupported
      ? WrongNetworkModal(chain?.name ?? '', WEB3_CONFIG.chains.bsc.id)
      : VoteModal(props);

    setModal(modal);
  };

  const handleClickGetMoreStars = () => {
    setModal(createBuyStarsModal());
  };

  const collectRewards = () => {
    setModal(
      CollectRewardsSingleRoundModal(
        currentRound,
        userStars,
        userStarsReward,
        handleClaimRewards,
        totalNumRounds.toString(),
      ),
    );
  };

  // Might want to make this a context
  const setUpVoting = async () => {
    const {
      pastRounds: pastRoundsState,
      futureRounds: futureRoundsState,
      currentRound: currentRoundsState,
      totalRounds,
    } = await getMovieVotingRounds();

    if (
      pastRoundsState.length === 0 &&
      currentRoundsState === undefined &&
      futureRoundsState.length === 0
    ) {
      setLoading(false);
      return;
    }

    const financedDataResponse = await projectsApi.listProjectsPublic({
      limit: 5,
      sort: ProjectSort.CreatedAtDesc,
      filter: ProjectFilter.Financed,
    });
    const financedMovies = financedDataResponse.data.data.map(movieData => {
      setFinancedMovieIds(prev => [...prev, movieData.id]);
      return {
        image: movieData.posterImage?.publicUrl ?? '',
        title: movieData.title,
        studio: movieData.studio ?? '',
        viewOnClick: handleViewOnClick,
      } as FinancedMovieData;
    });
    setFinancedMovieData(financedMovies);

    if (!currentRoundsState) {
      return;
    }

    setCurrentRound(currentRoundsState);
    setPastRounds(pastRoundsState);
    setFutureRounds(futureRoundsState);
    setTotalNumRounds(parseInt(totalRounds, 10));

    for (const movieId of currentRoundsState.movieIds) {
      const nft = await getNftIfMinted(
        MOVIE_VOTING_NFT_ADDRESS[walletDetails.chain.type],
        movieId,
        [NftInclude.Project],
      );

      const movieData = nft?.project;
      const result = {
        description: movieData?.description ?? '',
        genre: movieData?.genre ?? '',
        image: movieData?.posterImage?.publicUrl ?? '',
        name: movieData?.title ?? '',
        studio: movieData?.studio ?? '',
      };
      setNFTMetaData(prev => ({ ...prev, [movieId]: result }));

      const crewData = (movieData?.crew ?? []) as CastAndCrewData[];
      const castData = (movieData?.cast ?? []) as CastAndCrewData[];
      const castAndCrewData: CastAndCrewData[] = crewData.concat(
        castData.map(castObj => ({ ...castObj, role: 'Actor' })),
      );

      setCast(prev => ({ ...prev, [movieId]: castAndCrewData }));

      const userVotedResult = await getUserStarsForMovie(
        currentRoundsState.roundId,
        movieId,
      );
      setUserStars(prev => [...prev, userVotedResult]);
      setUserVotedState(prev => ({
        ...prev,
        [movieId]: Math.round(
          Math.sqrt(parseFloat(userVotedResult)),
        ).toString(),
      }));
      const reward = await calculateStarsRewards(currentRoundsState.roundId);
      setUserStarsReward(reward);
    }

    const block = await getCurrentBlock();
    const startBlockNumber = parseInt(currentRoundsState.startBlock, 10);
    const endBlockNumber = parseInt(currentRoundsState.endBlock, 10);
    const endBlockDifference = endBlockNumber - block.number;
    const startDifference = startBlockNumber - block.number;

    const startBlockTimestamp = await getBlockTimestamp(startBlockNumber);
    const endBlockTimestamp = await getBlockTimestamp(endBlockNumber);

    setCurrentBlock(block);
    setStartBlock({
      timestamp: startBlockTimestamp,
      number: startBlockNumber,
    });
    setEndBlock({
      timestamp: endBlockTimestamp,
      number: endBlockNumber,
    });
    setRoundStarted(block.number > startBlockNumber);
    setRoundEnded(block.number > endBlockNumber);
    if (block.number > endBlockNumber && futureRounds.length === 0) {
      setIsVotingEnded(true);
    }

    // 3.01s for average block time (BSC)
    if (startDifference > 0) {
      const endDate = new Date(
        Date.now() + startDifference * 1000 * AVERAGE_BLOCK_TIME_BSC,
      );
      setEstimatedEndDate(endDate);
    } else if (endBlockDifference > 0) {
      const endDate = new Date(
        Date.now() + endBlockDifference * 1000 * AVERAGE_BLOCK_TIME_BSC,
      );
      setEstimatedEndDate(endDate);
      setReloadTimer(new Date(endDate.valueOf() - 1000 * 60 * 60));
    } else {
      const endDate = new Date(endBlockTimestamp * 1000);
      setEstimatedEndDate(endDate);
    }
  };

  React.useEffect(() => {
    setLoading(true);

    if (!initialized) {
      return;
    }

    void setUpVoting().then(() => setLoading(false));
  }, [initialized, walletDetails]);

  const handleBnbSwitch = useCallback(
    () => switchNetworkAsync?.(WEB3_CONFIG.chains.bsc.id),
    [switchNetworkAsync],
  );

  const handleMainnetSwitch = useCallback(
    () => switchNetworkAsync?.(WEB3_CONFIG.chains.eth.id),
    [switchNetworkAsync],
  );

  if (chain?.unsupported && !isLoading) {
    return (
      <div className={styles.movieVotingPage}>
        <div className={styles.movieVotingContainer + ' max-w-screen-lg mt-10'}>
          <div className="flex flex-col items-center justify-center w-full h-full">
            <div className="flex flex-col items-center justify-center h-full max-w-screen-lg w-4/5">
              <p className="text-lg">You are using the wrong network ...</p>
              <Button className="mt-8" onClick={handleBnbSwitch}>
                Switch To BNB Chain
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (
    currentRound.movieIds.length === 0 &&
    futureRounds.length === 0 &&
    pastRounds.length === 0 &&
    !loading
  ) {
    return (
      <div className={styles.movieVotingPage}>
        <div className={styles.movieVotingContainer + ' max-w-screen-lg mt-10'}>
          <div className={'w-full flex justify-between'}>
            <div>
              <div
                className={
                  styles.voting +
                  ' 2xl:text-7xl xl:text-6xl lg:text-5xl text-4xl'
                }
              >
                VOTING
              </div>
            </div>
          </div>
          <div className="py-10">
            <div
              className={
                styles.subheader + ' w-full lg:w-full pb-3 text-base xl:text-lg'
              }
            >
              The Mogul voting contract uses a quadratic formula to calculate
              how many votes each $MOGUL holder gets when voting on which
              project Mogul will finance.
            </div>
            <div
              className={
                styles.subheader + ' w-full lg:w-full pb-3 text-base xl:text-lg'
              }
            >
              The number of votes a $MOGUL holder receives is equal to the
              square root of the number of $MOGUL they have.
            </div>
            <div
              className={
                styles.subheader + ' w-full lg:w-full text-base xl:text-lg'
              }
            >
              For more on how quadratic voting works, visit Mogul Production's{' '}
              <a
                className="underline"
                href="https://mogulproductions.gitbook.io/mogul-productions/product-information/voting"
              >
                Gitbook
              </a>
              .
            </div>
          </div>
          <div
            className={
              styles.subheader + ' w-full lg:w-full pb-3 text-base xl:text-lg'
            }
            style={{ fontWeight: 'bold' }}
          >
            There are currently no active voting rounds, please check back
            later.
          </div>
          <div
            className={
              styles.subheader + ' w-full lg:w-full pt-3 text-base xl:text-lg'
            }
            style={{ fontWeight: 'bold' }}
          >
            Check out the previous voting round!
          </div>
          <Button className="mt-5" onClick={handleMainnetSwitch}>
            Switch To ETH Chain
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="w-full h-full grid place-items-center">
        <CircularProgress className={classes.loader} size={100} />
      </div>
    );
  }

  const countdownHeader = roundEnded
    ? 'VOTING ENDED'
    : roundStarted
    ? 'ESTIMATED TIME LEFT'
    : 'VOTING STARTS IN';

  const countdownTooltip = roundEnded
    ? `Voting ended at Ethereum block #${currentRound.endBlock}.`
    : roundStarted
    ? `This is an estimate, the countdown will end at Ethereum block #${currentRound.endBlock}.`
    : `This is an estimate, the countdown will start at Ethereum block #${currentRound.endBlock}.`;

  return (
    <div className={styles.movieVotingPage}>
      <div
        className={
          styles.movieVotingContainer + ' max-w-screen-lg sm:mt-10 mt-20'
        }
      >
        <div className={'w-full flex flex-col sm:flex-row justify-between'}>
          <div>
            {!(isVotingEnded ?? false) && (
              <div className={styles.round}>
                ROUND {pastRounds.length + 1} OF
                {` ${totalNumRounds}`}
              </div>
            )}
            {!(isVotingEnded ?? false) ? (
              <div
                className={
                  styles.voting +
                  ' 2xl:text-7xl xl:text-6xl lg:text-5xl text-4xl'
                }
              >
                VOTING
              </div>
            ) : (
              <div
                className={
                  styles.voting +
                  ' 2xl:text-7xl xl:text-6xl lg:text-5xl text-4xl'
                }
              >
                PREVIOUS VOTING WINNER
              </div>
            )}
          </div>
          <div className="mt-5 sm:mt-0">
            <div className={styles.timeLeftTitle}>
              {countdownHeader}
              <Tooltip
                title={countdownTooltip}
                placement="top-end"
                classes={{ tooltip: classes.toolTips }}
              >
                <QuestionIcon className={styles.questionMarkToolTip} />
              </Tooltip>
            </div>
            {roundEnded ? (
              <div className={styles.timeLeft}>
                {estimatedEndDate.toLocaleString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </div>
            ) : roundAboutToEnd && roundStarted ? (
              <div className={styles.timeLeft}>ENDING SOON</div>
            ) : (
              <CountDown
                date={estimatedEndDate}
                reloadTimer={reloadTimer}
                reloadCallBack={reloadCallBack}
                secs={false}
              />
            )}
          </div>
        </div>
        <div className="py-10">
          <div
            className={
              styles.subheader + ' w-full lg:w-full pb-3 text-base xl:text-lg'
            }
          >
            The Mogul voting contract uses a quadratic formula to calculate how
            many votes each $MOGUL holder gets when voting on which Mogul will
            finance.
          </div>
          <div
            className={
              styles.subheader + ' w-full lg:w-full pb-3 text-base xl:text-lg'
            }
          >
            The number of votes a $MOGUL holder receives is equal to the square
            root of the number of $MOGUL they have.
          </div>
          <div
            className={
              styles.subheader + ' w-full lg:w-full text-base xl:text-lg'
            }
          >
            For more on how quadratic voting works, visit Mogul Production's{' '}
            <a
              className="underline"
              href="https://mogulproductions.gitbook.io/mogul-productions/product-information/voting"
            >
              Gitbook
            </a>
            .
          </div>
        </div>
        {currentRound.votingState === VotingRoundState.Paused && (
          <div className="my-10">
            <NotificationCard
              title={'Voting Paused'}
              body={
                'Voting and unvoting has been temporarily disabled. You may still collect your deposited STARS and STARS rewards at the end of the voting period.'
              }
            />
          </div>
        )}
        {parseInt(currentRound.endBlock, 10) < currentBlock.number &&
          userStarsReward !== '0' && (
            <div className="my-10">
              <NotificationCard
                title={'Unclaimed Rewards'}
                body={'Make sure to collect all unclaimed rewards from voting.'}
                isRow={true}
              >
                <Button className={'flex h-12'} onClick={collectRewards}>
                  <BlackStarIcon className="mr-2" />
                  COLLECT UNCLAIMED STARS
                </Button>
              </NotificationCard>
            </div>
          )}

        <div className="my-10 ">
          <ProjectVoting
            isEnded={isVotingEnded}
            noVoteButton={roundEnded || !roundStarted}
            movieIds={currentRound.movieIds}
            roundId={currentRound.roundId.toString()}
            userVotes={userVotedState}
            totalVotes={currentRound.votes}
            movieMetadata={NFTMetaData}
            openseaLink={openseaLink}
            displayAsCarousel={false}
            handleViewOnClick={handleViewOnClick}
            handleVoteOnClick={handleVoteOnClick}
            setUpVoting={setUpVoting}
            currentBlock={currentBlock}
            startBlock={startBlock}
            endBlock={endBlock}
            getMoreStars={handleClickGetMoreStars}
            rewardsPerBlock={
              parseFloat(currentRound.starsRewards) /
              (parseInt(currentRound.endBlock, 10) -
                parseInt(currentRound.startBlock, 10))
            }
            cast={cast}
            // these values get overwrite in the ProjectVoting
            numberOfVotes={'200'}
            starsSubmitted={'40001'}
            rewardGenerated={'20'}
            placeVote={(_: string) => {
              return Promise.resolve(true);
            }}
            withdrawStars={() => {}}
          />
        </div>
        {futureRounds.length !== 0 && (
          <div className="my-10">
            <OtherRounds
              handleClaimRewards={handleClaimRewards}
              rounds={futureRounds}
              totalNumRounds={totalNumRounds}
              isFutureRounds={true}
              startingRoundNum={totalNumRounds - futureRounds.length}
            />
          </div>
        )}

        {pastRounds.length !== 0 && (
          <div className="my-10">
            <OtherRounds
              handleClaimRewards={handleClaimRewards}
              rounds={pastRounds}
              totalNumRounds={totalNumRounds}
              isFutureRounds={false}
              startingRoundNum={
                totalNumRounds - futureRounds.length - 1 - pastRounds.length
              }
            />
          </div>
        )}

        {authenticated && financedMovieData.length > 0 && (
          <div className="mb-10">
            <div
              className={
                styles.financedTitle +
                ' mb-8 2xl:text-7xl xl:text-6xl lg:text-5xl text-4xl'
              }
            >
              PROJECTS BY MOGUL
            </div>
            <FinancedByMogul
              movies={financedMovieData}
              movieIds={financedMovieIds}
              handleViewOnClick={handleFianancedViewOnClick}
            />
          </div>
        )}
      </div>
    </div>
  );
};
